import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SearchInput } from '../../commons';
import { sendEventToJumbo } from '../../../analytics/jumboClient';
import { useHistory, useLocation } from 'react-router-dom';
import {
  EventType,
  FeatureName,
  MetricsForJumboEvent,
} from '../../../analytics/constants';
import { searchEvent, searchFilterChange } from '../../../analytics/events';
import {
  defaultOrgIdValue,
  ORG_ID_PARAM,
  orgIdKey,
  orgValKey,
} from '../OrgIdDropdown/utils';
import { SearchInputV1 } from '../../commons/SearchInput/SearchInputV1';

export const SearchOrderV1Comp = ({
  searchType,
  searchValue,
  resetState = () => {},
  setShowEmptyState = () => {},
  setSearchType = () => {},
  setSearchValue = () => {},
  getSearchOrders = () => {},
  setLoading = () => {},
}) => {
  const inputRef = useRef(null);
  const history = useHistory();
  const search = useLocation().search;

  const [currentOrg, setCurrentOrg] = useState(
    sessionStorage.getItem(orgIdKey),
  );

  const urlParams = new URLSearchParams(window.location.search);
  const orgIdFromUrl = urlParams.get(ORG_ID_PARAM);

  useEffect(() => {
    const handleOrgIdChange = (event) => {
      const { orgId, orgName } = event.detail;
      sessionStorage.setItem(orgIdKey, orgId);
      sessionStorage.setItem(orgValKey, orgName);
      setCurrentOrg(orgId);
    };
    window.addEventListener('orgIdChange', handleOrgIdChange);
    return () => {
      window.removeEventListener('orgIdChange', handleOrgIdChange);
    };
  }, []);

  useEffect(() => {
    if (orgIdFromUrl !== null && orgIdFromUrl !== currentOrg) {
      window.location.reload();
    }
  }, [currentOrg, orgIdFromUrl]);

  const setQueryParams = useCallback(() => {
    const path = history.location.pathname;
    let finalValue = searchValue || '';
    if (searchType === 'user_number') {
      finalValue = encodeURIComponent(searchValue);
    }
    history.replace(`${path}?type=${searchType}&value=${finalValue}`);
  }, [history, searchType, searchValue]);

  const searchOrderInfo = useCallback(
    (type, value) => {
      let selectedVal = value || searchValue,
        selectedType = type || searchType;
      let queryStrings = {};
      resetState(true);
      if (!selectedVal.length && !value) return;
      searchEvent({
        event_type: EventType.CLICK,
        feature_name: FeatureName.SEARCH,
        feature_meta: {
          search_filter: selectedType,
          search_value: selectedVal,
        },
        [MetricsForJumboEvent.customer_id]:
          selectedType === 'user_id' ? selectedVal : '',
        [MetricsForJumboEvent.order_id]:
          selectedType === 'order_id' ? selectedVal : '',
        [MetricsForJumboEvent.cart_id]:
          selectedType === 'cart_id' ? selectedVal : '',
      });
      queryStrings['type'] = selectedType;
      queryStrings['value'] = selectedVal;
      queryStrings = {
        ...queryStrings,
        orgId: currentOrg ? currentOrg : defaultOrgIdValue,
      };
      getSearchOrders(queryStrings);
      setShowEmptyState(false);
      setLoading(true);
    },
    [
      searchValue,
      searchType,
      resetState,
      getSearchOrders,
      setShowEmptyState,
      setLoading,
      currentOrg,
    ],
  );

  const getOrderDetails = useCallback(() => {
    let mode = window.dashboard.mode;
    if (mode === 'lifelineZ') {
      let type = new URLSearchParams(search).get('type');
      let value = new URLSearchParams(search).get('value');
      if (type && value) {
        setSearchType(type);
        setSearchValue(value);
        searchOrderInfo(type, value);
      }
    }
  }, [search, setSearchType, setSearchValue, searchOrderInfo]);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  useEffect(() => {
    getOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeInput = (event) => {
    const val = event.target.value;
    const searchValue =
      val && typeof val === 'string' ? val.replace(/,/g, '') : val;
    setSearchValue(searchValue);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      let mode = window.dashboard.mode;
      if (mode === 'lifelineZ') {
        setQueryParams();
      }
      searchOrderInfo();
    }
    return;
  };

  const selectDropdown = (value) => {
    sendEventToJumbo(`selected_${value}_to_search`);
    if (inputRef?.current) {
      inputRef.current.focus();
    }
    searchFilterChange({
      feature_meta: {
        filter_type: value,
      },
    });
    resetState(true);
    setShowEmptyState(true);
    setSearchValue('');
    setSearchType(value);
  };

  const handleClick = useCallback(() => {
    let mode = window.dashboard.mode;
    if (mode === 'lifelineZ') {
      setQueryParams();
    }
    searchOrderInfo();
  }, [searchOrderInfo, setQueryParams]);

  return (
    <SearchInputV1
      dropdownVal={searchType}
      inputVal={searchValue}
      onChange={changeInput}
      onSelect={selectDropdown}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      ref={inputRef}
    />
  );
};
